import { createContext, useContext } from "react";
import { LayoutGrid } from "lucide-react";
import type { LinkAdapter } from "./LinkAdapter";
import type { NavCatalogue, NavItem, NavModel, NavSection } from "./types";

// ── Task areas, More tools and the locked-feature catalogue ───────────────
// A shell builds its NavModel the way the sections read to a person: a few
// task areas ("Documents", "Resilience"), each a collapsible sidebar group whose
// pages are its rows (with their own badges) and also tabs above the page; the
// group holding the current page opens itself. finaliseNavModel then takes out of
// the sidebar everything that is not for this organisation *yet*:
//   - `deferred` pages (an optional register it has not used, a regime that
//     does not apply) and
//   - `lock`ed pages (a higher service tier)
// and lists them on the More tools page behind one row, so a dozen padlocks
// and unused registers cost the sidebar one line. Nothing here is access
// control: every route stays reachable and the API enforces tier and
// permission exactly as before (docs/NAVIGATION.md section 2).

export interface FinaliseOptions {
  /** Shell-relative path of the More tools page; no row is added without it. */
  moreToolsTo?: string;
  /** Section the More tools row joins (its last row); a new unlabelled section otherwise. */
  moreToolsSection?: string;
}

function pathOnly(to: string | undefined): string | undefined {
  if (!to) return undefined;
  const cut = to.search(/[?#]/);
  return cut === -1 ? to : to.slice(0, cut);
}

export function finaliseNavModel(model: NavModel, options: FinaliseOptions = {}): NavModel {
  const catalogue: NavCatalogue = { deferred: [], locked: [] };
  const file = (item: NavItem, area?: string) => {
    if (item.lock) catalogue.locked.push({ item, area });
    else catalogue.deferred.push({ item, area });
  };

  const sections: NavSection[] = [];
  for (const section of model.sections) {
    const items: NavItem[] = [];
    for (const item of section.items) {
      if (item.hidden) continue;
      if (item.tabs) {
        const shown: NavItem[] = [];
        for (const tab of item.tabs) {
          if (tab.hidden) continue;
          if (tab.deferred || tab.lock) { file(tab, item.label); continue; }
          shown.push(tab);
        }
        if (shown.length === 0) continue;
        const own = pathOnly(item.to);
        const landing = own && shown.some((t) => pathOnly(t.to) === own) ? item.to : shown[0].to;
        items.push({
          ...item,
          tabs: shown,
          // In the sidebar the area is a collapsible group of its pages, each row with its own
          // badge; a single page needs no group, so the area is then a plain row linking to it.
          children: shown.length > 1 ? shown : undefined,
          defaultOpen: item.defaultOpen ?? false,
          to: landing,
          activeMatch: [
            ...shown.map((t) => t.to).filter((t): t is string => !!t),
            ...shown.flatMap((t) => t.activeMatch ?? []),
          ],
          badge: item.badge ?? shown.find((t) => t.badge)?.badge,
          exact: false,
        });
        continue;
      }
      if (item.deferred || item.lock) { file(item, section.label); continue; }
      items.push(item);
    }
    if (items.length > 0) sections.push({ ...section, items });
  }

  const extras = catalogue.deferred.length + catalogue.locked.length;
  if (options.moreToolsTo && extras > 0) {
    const row: NavItem = {
      id: "more-tools",
      label: "More tools",
      to: options.moreToolsTo,
      icon: LayoutGrid,
      keywords: ["modules", "add-ons", "upgrade", "unlock", "registers", "all pages"],
    };
    const host = sections.find((s) => s.id === options.moreToolsSection);
    if (host) host.items = [...host.items, row];
    else sections.push({ id: "more-tools", items: [row] });
  }

  return { ...model, sections, catalogue };
}

/**
 * The area whose tabs hold the current page, and which of its tabs is current
 * (the longest matching path, so /resilience/incidents beats /resilience).
 */
export function activeArea(model: NavModel, adapter: LinkAdapter): { area: NavItem; tab: NavItem } | null {
  let best: { area: NavItem; tab: NavItem; weight: number } | null = null;
  for (const section of model.sections) {
    for (const area of section.items) {
      for (const tab of area.tabs ?? []) {
        if (!tab.to || !adapter.isActive(tab.to, tab.activeMatch, tab.exact)) continue;
        const weight = (pathOnly(tab.to) ?? "").length;
        if (!best || weight > best.weight) best = { area, tab, weight };
      }
    }
  }
  return best ? { area: best.area, tab: best.tab } : null;
}

// ── Context: the finalised model for pages inside the shell ───────────────

export interface NavModelValue {
  model: NavModel | null;
  adapter: LinkAdapter | null;
  /** Opens the tier lock dialog for a locked page (the More tools page uses it). */
  onLocked: (item: NavItem) => void;
}

export const NavModelContext = createContext<NavModelValue>({ model: null, adapter: null, onLocked: () => {} });

export function useNavModel(): NavModelValue {
  return useContext(NavModelContext);
}
